export type WorkflowV2QueueSla = "ok" | "warn" | "degraded";

export type WorkflowV2CapacitySignal = "workflow_capacity_deferred" | "workflow_fairness_throttled";

export type WorkflowV2CapacityReason =
  | "global_concurrency"
  | "app_concurrency"
  | "user_concurrency"
  | "workflow_name_concurrency";

export interface WorkflowV2CapacityCandidate {
  id: string;
  appId?: string;
  userId?: string | null;
  workflowName: string;
  priority: number;
  runnableAt: string | Date;
}

export interface WorkflowV2CapacitySnapshot {
  now: string | Date;
  globalRunning: number;
  runningByAppId?: Record<string, number>;
  runningByUserId?: Record<string, number>;
  runningByWorkflowName: Record<string, number>;
}

export interface WorkflowV2CapacityPolicy {
  globalConcurrency?: number | null;
  perAppConcurrency?: number | null;
  perUserConcurrency?: number | null;
  perWorkflowNameConcurrency?: number | null;
  priorityAgingIntervalMs?: number;
  priorityAgingMaxBoost?: number;
  queueSlaWarnMs?: number;
  queueSlaDegradedMs?: number;
  retryAfterMs?: number;
}

export type WorkflowV2CapacityDecision =
  | {
      allowed: true;
      effectivePriority: number;
      queueAgeMs: number;
      sla: WorkflowV2QueueSla;
    }
  | {
      allowed: false;
      reason: WorkflowV2CapacityReason;
      signal: WorkflowV2CapacitySignal;
      retryAfterMs: number;
      effectivePriority: number;
      queueAgeMs: number;
      sla: WorkflowV2QueueSla;
    };

const DEFAULT_PRIORITY_AGING_INTERVAL_MS = 60_000;
const DEFAULT_PRIORITY_AGING_MAX_BOOST = 10;
const DEFAULT_QUEUE_SLA_WARN_MS = 60_000;
const DEFAULT_QUEUE_SLA_DEGRADED_MS = 300_000;
const DEFAULT_RETRY_AFTER_MS = 1_000;

const DEFAULT_CAPACITY_POLICY: Required<WorkflowV2CapacityPolicy> = {
  globalConcurrency: 20,
  perAppConcurrency: 8,
  perUserConcurrency: 3,
  perWorkflowNameConcurrency: 3,
  priorityAgingIntervalMs: DEFAULT_PRIORITY_AGING_INTERVAL_MS,
  priorityAgingMaxBoost: DEFAULT_PRIORITY_AGING_MAX_BOOST,
  queueSlaWarnMs: DEFAULT_QUEUE_SLA_WARN_MS,
  queueSlaDegradedMs: DEFAULT_QUEUE_SLA_DEGRADED_MS,
  retryAfterMs: DEFAULT_RETRY_AFTER_MS,
};

function toTime(value: string | Date): number {
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function isFinitePositive(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function readPositiveInteger(env: Record<string, string | undefined>, key: string): number | undefined {
  const raw = env[key];
  if (!raw) return undefined;
  const value = Number(raw);
  if (!Number.isInteger(value) || value <= 0) return undefined;
  return value;
}

function hasExplicitCapacityEnv(env: Record<string, string | undefined>): boolean {
  return [
    "GENCOW_WORKFLOW_GLOBAL_CONCURRENCY",
    "GENCOW_WORKFLOW_PER_APP_CONCURRENCY",
    "GENCOW_WORKFLOW_PER_USER_CONCURRENCY",
    "GENCOW_WORKFLOW_PER_NAME_CONCURRENCY",
    "GENCOW_WORKFLOW_QUEUE_SLA_WARN_MS",
    "GENCOW_WORKFLOW_QUEUE_SLA_DEGRADED_MS",
    "GENCOW_WORKFLOW_PRIORITY_AGING_INTERVAL_MS",
    "GENCOW_WORKFLOW_PRIORITY_AGING_MAX_BOOST",
    "GENCOW_WORKFLOW_CAPACITY_RETRY_AFTER_MS",
  ].some((key) => env[key] != null && env[key] !== "");
}

export function loadWorkflowV2CapacityPolicyFromEnv(
  env: Record<string, string | undefined> = process.env,
  options: { useDefaults?: boolean } = {},
): WorkflowV2CapacityPolicy | undefined {
  if (env.GENCOW_WORKFLOW_CAPACITY_DISABLED === "true") return undefined;
  if (!options.useDefaults && !hasExplicitCapacityEnv(env)) return undefined;

  const base: WorkflowV2CapacityPolicy = options.useDefaults ? DEFAULT_CAPACITY_POLICY : {};
  return {
    globalConcurrency:
      readPositiveInteger(env, "GENCOW_WORKFLOW_GLOBAL_CONCURRENCY") ?? base.globalConcurrency,
    perAppConcurrency:
      readPositiveInteger(env, "GENCOW_WORKFLOW_PER_APP_CONCURRENCY") ?? base.perAppConcurrency,
    perUserConcurrency:
      readPositiveInteger(env, "GENCOW_WORKFLOW_PER_USER_CONCURRENCY") ?? base.perUserConcurrency,
    perWorkflowNameConcurrency:
      readPositiveInteger(env, "GENCOW_WORKFLOW_PER_NAME_CONCURRENCY") ?? base.perWorkflowNameConcurrency,
    queueSlaWarnMs: readPositiveInteger(env, "GENCOW_WORKFLOW_QUEUE_SLA_WARN_MS") ?? base.queueSlaWarnMs,
    queueSlaDegradedMs:
      readPositiveInteger(env, "GENCOW_WORKFLOW_QUEUE_SLA_DEGRADED_MS") ?? base.queueSlaDegradedMs,
    priorityAgingIntervalMs:
      readPositiveInteger(env, "GENCOW_WORKFLOW_PRIORITY_AGING_INTERVAL_MS") ?? base.priorityAgingIntervalMs,
    priorityAgingMaxBoost:
      readPositiveInteger(env, "GENCOW_WORKFLOW_PRIORITY_AGING_MAX_BOOST") ?? base.priorityAgingMaxBoost,
    retryAfterMs: readPositiveInteger(env, "GENCOW_WORKFLOW_CAPACITY_RETRY_AFTER_MS") ?? base.retryAfterMs,
  };
}

export function getWorkflowV2QueueAgeMs(candidate: WorkflowV2CapacityCandidate, now: string | Date): number {
  return Math.max(0, toTime(now) - toTime(candidate.runnableAt));
}

export function classifyWorkflowV2QueueSla(
  ageMs: number,
  policy: WorkflowV2CapacityPolicy = {},
): WorkflowV2QueueSla {
  const degradedMs = Math.max(0, policy.queueSlaDegradedMs ?? DEFAULT_QUEUE_SLA_DEGRADED_MS);
  const warnMs = Math.max(0, policy.queueSlaWarnMs ?? DEFAULT_QUEUE_SLA_WARN_MS);
  if (ageMs >= degradedMs) return "degraded";
  if (ageMs >= warnMs) return "warn";
  return "ok";
}

export function getWorkflowV2EffectivePriority(
  candidate: WorkflowV2CapacityCandidate,
  policy: WorkflowV2CapacityPolicy,
  now: string | Date,
): number {
  const ageMs = getWorkflowV2QueueAgeMs(candidate, now);
  const intervalMs = Math.max(1, policy.priorityAgingIntervalMs ?? DEFAULT_PRIORITY_AGING_INTERVAL_MS);
  const maxBoost = Math.max(0, policy.priorityAgingMaxBoost ?? DEFAULT_PRIORITY_AGING_MAX_BOOST);
  return candidate.priority + Math.min(maxBoost, Math.floor(ageMs / intervalMs));
}

export function decideWorkflowV2Capacity(options: {
  candidate: WorkflowV2CapacityCandidate;
  snapshot: WorkflowV2CapacitySnapshot;
  policy?: WorkflowV2CapacityPolicy;
}): WorkflowV2CapacityDecision {
  const policy = options.policy ?? {};
  const queueAgeMs = getWorkflowV2QueueAgeMs(options.candidate, options.snapshot.now);
  const sla = classifyWorkflowV2QueueSla(queueAgeMs, policy);
  const effectivePriority = getWorkflowV2EffectivePriority(options.candidate, policy, options.snapshot.now);
  const retryAfterMs = Math.max(0, policy.retryAfterMs ?? DEFAULT_RETRY_AFTER_MS);

  if (
    isFinitePositive(policy.globalConcurrency) &&
    options.snapshot.globalRunning >= policy.globalConcurrency
  ) {
    return {
      allowed: false,
      reason: "global_concurrency",
      signal: "workflow_capacity_deferred",
      retryAfterMs,
      effectivePriority,
      queueAgeMs,
      sla,
    };
  }

  const appId = options.candidate.appId;
  const runningForApp = appId ? (options.snapshot.runningByAppId?.[appId] ?? 0) : 0;
  if (appId && isFinitePositive(policy.perAppConcurrency) && runningForApp >= policy.perAppConcurrency) {
    return {
      allowed: false,
      reason: "app_concurrency",
      signal: "workflow_fairness_throttled",
      retryAfterMs,
      effectivePriority,
      queueAgeMs,
      sla,
    };
  }

  const userId = options.candidate.userId;
  const runningForUser = userId ? (options.snapshot.runningByUserId?.[userId] ?? 0) : 0;
  if (userId && isFinitePositive(policy.perUserConcurrency) && runningForUser >= policy.perUserConcurrency) {
    return {
      allowed: false,
      reason: "user_concurrency",
      signal: "workflow_fairness_throttled",
      retryAfterMs,
      effectivePriority,
      queueAgeMs,
      sla,
    };
  }

  const runningForName = options.snapshot.runningByWorkflowName[options.candidate.workflowName] ?? 0;
  if (
    isFinitePositive(policy.perWorkflowNameConcurrency) &&
    runningForName >= policy.perWorkflowNameConcurrency
  ) {
    return {
      allowed: false,
      reason: "workflow_name_concurrency",
      signal: "workflow_fairness_throttled",
      retryAfterMs,
      effectivePriority,
      queueAgeMs,
      sla,
    };
  }

  return { allowed: true, effectivePriority, queueAgeMs, sla };
}

export function sortWorkflowV2CandidatesByFairness(
  candidates: WorkflowV2CapacityCandidate[],
  policy: WorkflowV2CapacityPolicy,
  now: string | Date,
): WorkflowV2CapacityCandidate[] {
  return [...candidates].sort((a, b) => {
    const priorityDiff =
      getWorkflowV2EffectivePriority(b, policy, now) - getWorkflowV2EffectivePriority(a, policy, now);
    if (priorityDiff !== 0) return priorityDiff;
    const runnableDiff = toTime(a.runnableAt) - toTime(b.runnableAt);
    if (runnableDiff !== 0) return runnableDiff;
    return a.id.localeCompare(b.id);
  });
}
